import React, { useContext, useEffect } from "react";
import { withRouter } from "react-router";
import { NavLink, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  CreditCard,
  Receipt,
  DollarSign,
  MessageCircle,
  Brain,
  Briefcase,
  ChevronRight,
  Menu,
  X,
  User,
  LogOut,
  Edit3,
} from "lucide-react";

import useModal from "hooks/use-modal";
import { useChatWidget } from "../chat-widget/chat-widget";
import { MainStore } from "../../stores/Main";
import PoweredByImg from "../../assets/svg/powered-by.svg";
import PoweredBy from "../../utils/poweredBy";
import DebtDisclosure from "../../components/debt-disclosure/debt-disclosure";
import FreedeLogo from "../../assets/svg/logo-dark.svg";
import TestLogo from "../../assets/svg/logo-testagency.svg";
import TestBLogo from "../../assets/svg/logo-testagencyb.svg";
import KFLogo from "../../assets/svg/logo-knutefinancial.svg";

const LayoutMain = (props) => {
  const { closeModal } = useModal();
  const {
    acceptedDisclosures,
    phoneVerified,
    agency,
    agencyDetails,
    profile,
    avatar,
    handleLogout,
  } = useContext(MainStore);

  let location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Dynamic logo selection based on hostname
  let logoPath = FreedeLogo;
  let logoHeight = "32";
  let logoWidth = "120";

  if (window.location.host.includes("testagencyb")) {
    logoPath = TestBLogo;
  } else if (
    window.location.host.includes("testagency") ||
    window.location.host.includes("freede-agency")
  ) {
    logoPath = TestLogo;
  } else if (window.location.host.includes("knute")) {
    logoPath = KFLogo;
    logoHeight = "28";
    logoWidth = "100";
  }

  useEffect(() => {
    closeModal();
    setMobileMenuOpen(false);
  }, [location.pathname]); // Only depend on pathname, not the entire location object or closeModal

  // Group navigation items
  const mainNavItems =
    acceptedDisclosures && agency
      ? [
          {
            path: "/dashboard/accounts",
            label: "Accounts",
            icon: Users,
            mobileIcon: Users,
            show: true,
            exact: false,
          },
          {
            path: "/dashboard/payments",
            label: "Payments",
            icon: CreditCard,
            mobileIcon: CreditCard,
            show: true,
            exact: false,
          },
        ]
      : [];

  // Like mainNavItems, the services nav waits for disclosures: before
  // acceptance every one of these links just bounces back to the
  // disclosures page (dashboard-root redirect), so showing them is noise.
  const freedeServices = !acceptedDisclosures
    ? []
    : [
    {
      path: "/dashboard/lowerbills",
      label: "Lower Bills",
      icon: Receipt,
      mobileIcon: Receipt,
      show: true,
      exact: false,
    },
    {
      path: "/dashboard/financing",
      label: "Financing",
      icon: DollarSign,
      mobileIcon: DollarSign,
      show: true,
      exact: false,
    },
    {
      path: "/dashboard/wellness-coach",
      label: "Wellness Coach",
      icon: Brain,
      mobileIcon: Brain,
      show: true,
      exact: false,
    },
    {
      path: "/dashboard/job-search",
      label: "Job Search",
      icon: Briefcase,
      mobileIcon: Briefcase,
      show: true,
      exact: false,
    },
  ];

  // Chat is not a route anymore — it opens the persistent widget (the
  // conversation survives navigation, so there is no chat "page" to be on).
  // Gated EXACTLY like the widget itself (chat domain + disclosures accepted),
  // so the button never appears where the widget won't render — e.g. the
  // disclosures page, where the layout is already up but chat isn't allowed.
  const showChat = !!agencyDetails?.base_chat_domain && acceptedDisclosures;

  const { openChat } = useChatWidget();

  const doLogout = (e) => {
    e.preventDefault();
    handleLogout();
  };

  return (
    // h-screen (not min-h-screen): the column below splits into a scrollable
    // content area + the footer, and the footer only stays pinned on-screen
    // when the column is EXACTLY viewport height — with min-h, any page
    // taller than the viewport grows the column and shoves the footer below
    // the fold (the chat page's full-height iframe always did).
    <div className="h-screen bg-gradient-to-b from-[#071427] via-[#071427] to-[#06111f] flex">
      {/* Desktop Sidebar */}
      <motion.div
        className="hidden lg:flex w-64 flex-shrink-0"
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <div className="fixed left-0 top-0 h-full w-64 bg-white/[0.03] backdrop-blur-sm border-r border-white/10 flex flex-col">
          {/* Logo at top of sidebar */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center">
              <motion.img
                src={logoPath}
                alt={agencyDetails?.name || "Home"}
                height={logoHeight}
                width={logoWidth}
                draggable={false}
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400 }}
              />
            </Link>
          </div>

          {/* Profile Section in Sidebar */}
          {profile && profile.first_name && (
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center gap-4">
                <motion.div
                  className="relative"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  {avatar ? (
                    <img
                      src={`data:image/png;base64,${avatar}`}
                      alt={profile.first_name}
                      className="w-12 h-12 rounded-2xl border-2 border-white/20 shadow-lg"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <User className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-[#071427]" />
                </motion.div>
                <div className="flex-1 min-w-0">
                  {(() => {
                    const firstName = profile.first_name || "";
                    const lastName = profile.last_name || "";
                    const fullName = `${firstName} ${lastName}`.trim();

                    // If name is longer than 12 characters or any part is long, split it
                    if (
                      fullName.length > 12 ||
                      firstName.length > 10 ||
                      lastName.length > 10
                    ) {
                      return (
                        <div className="text-white font-medium leading-tight break-words">
                          <div className="break-all">{firstName}</div>
                          {lastName && (
                            <div className="break-all">{lastName}</div>
                          )}
                        </div>
                      );
                    }

                    return <p className="text-white font-medium">{fullName}</p>;
                  })()}
                  <p className="text-white/50 text-xs truncate">
                    {profile.email || ""}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-6">
              {/* Main Navigation - Accounts & Payments */}
              {mainNavItems.length > 0 && (
                <div>
                  <ul className="space-y-1">
                    {mainNavItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = item.exact
                        ? location.pathname === item.path
                        : location.pathname.startsWith(item.path);

                      return (
                        <motion.li
                          key={item.path}
                          whileHover={{ x: 4 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <NavLink
                            to={item.path}
                            className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 group relative ${
                              isActive
                                ? "bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-white shadow-lg border border-white/10"
                                : "text-white/70 hover:bg-white/5 hover:text-white"
                            }`}
                          >
                            {isActive && (
                              <motion.div
                                className="absolute left-0 w-1 h-6 bg-gradient-to-b from-blue-400 to-purple-600 rounded-r-full"
                                layoutId="sidebar-indicator"
                                transition={{
                                  type: "spring",
                                  stiffness: 350,
                                  damping: 30,
                                }}
                              />
                            )}
                            <Icon
                              className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-blue-400" : ""}`}
                            />
                            <span className="font-medium whitespace-nowrap">
                              {item.label}
                            </span>
                            <ChevronRight
                              className={`w-4 h-4 ml-auto flex-shrink-0 transition-all ${
                                isActive
                                  ? "opacity-100 translate-x-0"
                                  : "opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0"
                              }`}
                            />
                          </NavLink>
                        </motion.li>
                      );
                    })}
                  </ul>

                  {/* Chat lives under Freede Services below — a second
                      "Chat Now" entry here duplicated it. */}

                  {/* Separator */}
                  <div className="mt-4 mb-2 px-4">
                    <div className="h-px bg-white/10"></div>
                  </div>
                </div>
              )}

              {/* Freede Services Section */}
              {freedeServices.length > 0 && (
              <div>
                <div className="px-4 mb-2">
                  <p className="text-white/40 text-xs font-semibold uppercase tracking-wider">
                    Freede Services
                  </p>
                </div>
                <ul className="space-y-1">
                  {freedeServices.map((item) => {
                    const Icon = item.icon;
                    const isActive = item.exact
                      ? location.pathname === item.path
                      : location.pathname.startsWith(item.path);

                    return (
                      <motion.li
                        key={item.path}
                        whileHover={{ x: 4 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <NavLink
                          to={item.path}
                          className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 group relative ${
                            isActive
                              ? "bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-white shadow-lg border border-white/10"
                              : "text-white/70 hover:bg-white/5 hover:text-white"
                          }`}
                        >
                          {isActive && (
                            <motion.div
                              className="absolute left-0 w-1 h-6 bg-gradient-to-b from-blue-400 to-purple-600 rounded-r-full"
                              layoutId="sidebar-indicator"
                              transition={{
                                type: "spring",
                                stiffness: 350,
                                damping: 30,
                              }}
                            />
                          )}
                          <Icon
                            className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-blue-400" : ""}`}
                          />
                          <span className="font-medium whitespace-nowrap">
                            {item.label}
                          </span>
                          <ChevronRight
                            className={`w-4 h-4 ml-auto flex-shrink-0 transition-all ${
                              isActive
                                ? "opacity-100 translate-x-0"
                                : "opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0"
                            }`}
                          />
                        </NavLink>
                      </motion.li>
                    );
                  })}
                  {showChat && (
                    <motion.li whileHover={{ x: 4 }} whileTap={{ scale: 0.98 }}>
                      <button
                        type="button"
                        onClick={openChat}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 group relative text-white/70 hover:bg-white/5 hover:text-white"
                      >
                        <MessageCircle className="w-5 h-5 flex-shrink-0" />
                        <span className="font-medium whitespace-nowrap">
                          Chat
                        </span>
                        <ChevronRight className="w-4 h-4 ml-auto flex-shrink-0 transition-all opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0" />
                      </button>
                    </motion.li>
                  )}
                </ul>
              </div>
              )}
            </div>
          </nav>

          {/* Profile Actions at Bottom */}
          {profile && profile.first_name && (
            <div className="p-4 space-y-1">
              <Link
                to="/dashboard/profile"
                className="flex items-center gap-3 px-3 py-2 rounded-xl text-white/70 hover:bg-white/5 hover:text-white transition-all group"
              >
                <Edit3 className="w-4 h-4" />
                <span className="text-sm">Edit Profile</span>
              </Link>
              <button
                onClick={doLogout}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-white/70 hover:bg-white/5 hover:text-white transition-all group"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col p-0">
        {/* Mobile Header - Only visible on mobile */}
        <motion.header
          className="lg:hidden h-16 bg-[#1a2b47] border-b border-white/10 flex-shrink-0 sticky top-0 z-40"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-between h-full px-4">
            <Link to="/" className="flex items-center gap-2">
              <motion.img
                src={logoPath}
                alt={agencyDetails?.name || "Home"}
                height={logoHeight}
                width={logoWidth}
                draggable={false}
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400 }}
              />
            </Link>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 rounded-2xl bg-white/10 text-white hover:bg-white/20 transition-colors"
              whileTap={{ scale: 0.95 }}
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </motion.button>
          </div>
        </motion.header>

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-auto">
            {/* Background orbs */}
            <div
              aria-hidden
              className="pointer-events-none fixed -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-blue-500/10 blur-3xl"
            />

            {/* Page Content - Full Width. h-full (not just min-h) gives
                full-height pages (chat) a definite height reference to fill;
                taller pages still overflow and scroll within this area. */}
            <div className="relative z-10 h-full">{props.children}</div>
          </div>

          {/* Footer - Desktop Only - Fixed at bottom */}
          <footer className="relative z-10 border-t border-white/10 hidden lg:block flex-shrink-0">
            <div className="px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between flex-wrap gap-4">
                <div className="flex flex-col md:flex-row md:items-center gap-3 text-white/60 text-sm min-w-0">
                  {agencyDetails?.name && (
                    <>
                      <span>{agencyDetails.name}</span>
                      <span className="hidden md:inline">•</span>
                      <span>{agencyDetails.phone}</span>
                      <span className="hidden md:inline">•</span>
                      <span className="truncate">
                        {agencyDetails.address}, {agencyDetails.city}{" "}
                        {agencyDetails.state} {agencyDetails.zip}
                      </span>
                    </>
                  )}
                </div>
                {/* Powered by Freede */}
                {agency && PoweredBy && (
                  <img
                    src={PoweredByImg}
                    alt="Powered by Freede"
                    className="h-6 opacity-50 hover:opacity-70 transition-opacity flex-shrink-0"
                  />
                )}
              </div>
              <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between text-xs text-white/50">
                <div className="flex items-center gap-4">
                  {agency && (
                    <Link
                      to="/dashboard/disclosures"
                      className="underline hover:text-white transition-colors"
                    >
                      Consumer Rights & Disclosures
                    </Link>
                  )}
                  <a
                    className="underline hover:text-white transition-colors"
                    href="https://www.freede.co/legal-terms-platform"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms of Use
                  </a>
                  <a
                    className="underline hover:text-white transition-colors"
                    href="https://www.freede.co/legal-privacy-platform"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>
                </div>
                <div className="mt-2 md:mt-0">
                  © {new Date().getFullYear()} Freede Solutions Inc. All rights
                  reserved.
                </div>
              </div>
              <DebtDisclosure agencyDetails={agencyDetails} />
              {agencyDetails?.extra_footer && (
                <div
                  className="mt-3 text-center text-xs text-white/40 max-w-3xl mx-auto"
                  dangerouslySetInnerHTML={{
                    __html: agencyDetails.extra_footer,
                  }}
                />
              )}
            </div>
          </footer>
        </div>
      </div>

      {/* Mobile Slide-up Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#0a1929] border-t border-white/10 z-50 rounded-t-3xl max-h-[80vh] overflow-auto"
            >
              {/* Drag Handle */}
              <div className="flex justify-center py-3">
                <div className="w-12 h-1 bg-white/20 rounded-full" />
              </div>

              {/* Profile Section */}
              {profile && profile.first_name && (
                <div className="px-6 pb-4 border-b border-white/10">
                  <div className="flex items-center gap-4">
                    {avatar ? (
                      <img
                        src={`data:image/png;base64,${avatar}`}
                        alt={profile.first_name}
                        className="w-14 h-14 rounded-2xl border-2 border-white/20"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                        <User className="w-7 h-7 text-white" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      {(() => {
                        const firstName = profile.first_name || "";
                        const lastName = profile.last_name || "";
                        const fullName = `${firstName} ${lastName}`.trim();

                        // If name is longer than 12 characters or any part is long, split it
                        if (
                          fullName.length > 12 ||
                          firstName.length > 10 ||
                          lastName.length > 10
                        ) {
                          return (
                            <div className="text-white font-medium leading-tight break-words">
                              <div className="break-all">{firstName}</div>
                              {lastName && (
                                <div className="break-all">{lastName}</div>
                              )}
                            </div>
                          );
                        }

                        return (
                          <p className="text-white font-medium">{fullName}</p>
                        );
                      })()}
                      <p className="text-white/50 text-sm truncate">
                        {profile.email || ""}
                      </p>
                    </div>
                  </div>

                  {/* Profile Actions */}
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <Link
                      to="/dashboard/profile"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-all"
                    >
                      <Edit3 className="w-5 h-5" />
                      <span className="text-xs">Profile</span>
                    </Link>
                    <button
                      onClick={(e) => {
                        doLogout(e);
                        setMobileMenuOpen(false);
                      }}
                      className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-all"
                    >
                      <LogOut className="w-5 h-5" />
                      <span className="text-xs">Logout</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <nav className="p-4">
                <div className="space-y-4">
                  {/* Main Navigation - Accounts & Payments */}
                  {mainNavItems.length > 0 && (
                    <div>
                      <ul className="space-y-2">
                        {mainNavItems.map((item) => {
                          const Icon = item.icon;
                          const isActive = item.exact
                            ? location.pathname === item.path
                            : location.pathname.startsWith(item.path);

                          return (
                            <motion.li
                              key={item.path}
                              whileTap={{ scale: 0.98 }}
                            >
                              <NavLink
                                to={item.path}
                                onClick={() => setMobileMenuOpen(false)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 ${
                                  isActive
                                    ? "bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-white border border-white/10"
                                    : "text-white/70 hover:bg-white/5 hover:text-white"
                                }`}
                              >
                                <Icon
                                  className={`w-5 h-5 ${isActive ? "text-blue-400" : ""}`}
                                />
                                <span className="font-medium flex-1">
                                  {item.label}
                                </span>
                                {isActive && (
                                  <ChevronRight className="w-4 h-4 text-blue-400" />
                                )}
                              </NavLink>
                            </motion.li>
                          );
                        })}
                      </ul>

                      {/* Chat lives under Freede Services below — a second
                          "Chat Now" entry here duplicated it. */}

                      {/* Separator */}
                      <div className="mt-4 px-4">
                        <div className="h-px bg-white/10"></div>
                      </div>
                    </div>
                  )}

                  {/* Freede Services Section */}
                  {freedeServices.length > 0 && (
                  <div>
                    <div className="px-4 mb-2">
                      <p className="text-white/40 text-xs font-semibold uppercase tracking-wider">
                        Freede Services
                      </p>
                    </div>
                    <ul className="space-y-2">
                      {freedeServices.map((item) => {
                        const Icon = item.icon;
                        const isActive = item.exact
                          ? location.pathname === item.path
                          : location.pathname.startsWith(item.path);

                        return (
                          <motion.li
                            key={item.path}
                            whileTap={{ scale: 0.98 }}
                          >
                            <NavLink
                              to={item.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 ${
                                isActive
                                  ? "bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-white border border-white/10"
                                  : "text-white/70 hover:bg-white/5 hover:text-white"
                              }`}
                            >
                              <Icon
                                className={`w-5 h-5 ${isActive ? "text-blue-400" : ""}`}
                              />
                              <span className="font-medium flex-1">
                                {item.label}
                              </span>
                              {isActive && (
                                <ChevronRight className="w-4 h-4 text-blue-400" />
                              )}
                            </NavLink>
                          </motion.li>
                        );
                      })}
                      {showChat && (
                        <motion.li whileTap={{ scale: 0.98 }}>
                          <button
                            type="button"
                            onClick={() => {
                              setMobileMenuOpen(false);
                              openChat();
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 text-white/70 hover:bg-white/5 hover:text-white"
                          >
                            <MessageCircle className="w-5 h-5" />
                            <span className="font-medium flex-1 text-left">
                              Chat
                            </span>
                          </button>
                        </motion.li>
                      )}
                    </ul>
                  </div>
                  )}
                </div>
              </nav>

              {/* Footer Info */}
              <div className="px-6 py-4 border-t border-white/10 text-center">
                {/* Powered by Freede */}
                {PoweredBy && (
                  <img
                    src={PoweredByImg}
                    alt="Powered by Freede"
                    className="h-6 opacity-50 hover:opacity-70 transition-opacity mx-auto mb-3"
                  />
                )}

                <div className="text-xs text-white/40">
                  {agencyDetails?.name && (
                    <div className="mb-2">
                      {agencyDetails.name} • {agencyDetails.phone}
                    </div>
                  )}
                  <div className="flex justify-center gap-4">
                    {agency && (
                      <Link
                        to="/dashboard/disclosures"
                        onClick={() => setMobileMenuOpen(false)}
                        className="underline hover:text-white/60"
                      >
                        Consumer Rights & Disclosures
                      </Link>
                    )}
                    <a
                      href="https://www.freede.co/legal-terms-platform"
                      className="underline hover:text-white/60"
                    >
                      Terms
                    </a>
                    <a
                      href="https://www.freede.co/legal-privacy-platform"
                      className="underline hover:text-white/60"
                    >
                      Privacy
                    </a>
                  </div>
                </div>
                <DebtDisclosure agencyDetails={agencyDetails} />
                {agencyDetails?.extra_footer && (
                  <div
                    className="mt-3 text-center text-xs text-white/40 max-w-md mx-auto px-4"
                    dangerouslySetInnerHTML={{
                      __html: agencyDetails.extra_footer,
                    }}
                  />
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default withRouter(LayoutMain);
